/**
 * A configuration type as a form is built from it: the tree a fileset's registration carries for a
 * plugin's configuration (godev checkpointengine/typetree.go), one node per construct of the
 * checkpoint type. FEATURE-20261021-check-config-type-and-fileset-form.
 *
 *   record    a group of fields; `defaults` is what the type's completion fills in when nothing is given
 *   enum      a choice; the chosen case's fields open under it
 *   list      repeating values of one type
 *   nullable  a value that may be left out (T | Null)
 *   duration  a Duration: a number and a unit
 *   named     a type the tree does not open: a secret type, an opaque type
 */
export type TypeTree =
  | { kind: "str" | "num" | "bool" | "date" | "time" | "timestamp" | "money" | "duration" | "any" }
  | { kind: "list"; of: TypeTree }
  | { kind: "nullable"; of: TypeTree }
  | { kind: "record"; name?: string; module?: string; fields: TypeTreeField[]; defaults?: Record<string, unknown>; defaultsError?: string }
  | { kind: "tuple"; fields: TypeTreeField[] }
  | { kind: "enum"; name?: string; module?: string; cases: TypeTreeCase[] }
  | { kind: "named"; name: string; module?: string };

export type TypeTreeField = { name: string; type: TypeTree; hasDefault: boolean };

/**
 * A form section's note when working out the configuration's defaults failed: the record type's
 * completion raised when the plan was read (godev checkpoint instance.TypeTreeOf, defaultsError), so the
 * fields it fills in have no default; a value already stored still shows. A type with no defaults (no
 * completion, or one that fills nothing in) has no defaultsError and gets no note. undefined then.
 */
export function defaultsNote(config: TypeTree | undefined): { severity: "error"; message: string } | undefined {
  if (!config || config.kind !== "record" || !config.defaultsError) return undefined;
  const type = config.name ? `${config.module ? `${config.module}.` : ""}${config.name}` : "the configuration";
  return {
    severity: "error",
    message: `Working out the defaults of ${type} failed, so the fields it fills in have no default: ${config.defaultsError}`,
  };
}
export type TypeTreeCase = { name: string; fields: TypeTreeField[] };

const leafKinds = new Set(["str", "num", "bool", "date", "time", "timestamp", "money", "duration", "any"]);

function asObject(v: unknown): Record<string, unknown> | undefined {
  return v && typeof v === "object" && !Array.isArray(v) ? (v as Record<string, unknown>) : undefined;
}

function fieldsOf(raw: unknown): TypeTreeField[] {
  if (!Array.isArray(raw)) return [];
  const out: TypeTreeField[] = [];
  for (const item of raw) {
    const f = asObject(item);
    const type = f ? parseTypeTree(f["type"]) : undefined;
    if (!f || !type || typeof f["name"] !== "string") continue;
    out.push({ name: f["name"], type, hasDefault: Boolean(f["hasDefault"]) });
  }
  return out;
}

/** The tree a registration carries, or undefined when the registration has none or it does not parse. */
export function parseTypeTree(raw: unknown): TypeTree | undefined {
  const t = asObject(raw);
  if (!t || typeof t["kind"] !== "string") return undefined;
  const kind = t["kind"];
  if (leafKinds.has(kind)) return { kind } as TypeTree;
  const name = typeof t["name"] === "string" ? t["name"] : undefined;
  const module = typeof t["module"] === "string" ? t["module"] : undefined;
  switch (kind) {
    case "list":
    case "nullable": {
      const of = parseTypeTree(t["of"]);
      return of ? { kind, of } : undefined;
    }
    case "record": {
      const defaults = asObject(t["defaults"]);
      const defaultsError = typeof t["defaultsError"] === "string" ? t["defaultsError"] : "";
      return { kind, name, module, fields: fieldsOf(t["fields"]), ...(defaults ? { defaults } : {}), ...(defaultsError ? { defaultsError } : {}) };
    }
    case "tuple":
      return { kind, fields: fieldsOf(t["fields"]) };
    case "enum": {
      const cases: TypeTreeCase[] = [];
      for (const item of Array.isArray(t["cases"]) ? t["cases"] : []) {
        const c = asObject(item);
        if (c && typeof c["name"] === "string") cases.push({ name: c["name"], fields: fieldsOf(c["fields"]) });
      }
      return { kind, name, module, cases };
    }
    case "named":
      return name ? { kind, name, module } : undefined;
    default:
      return undefined;
  }
}

/** A value of the type is one text box (a scalar or a name), not something that opens. */
export function isScalar(tree: TypeTree): boolean {
  return tree.kind === "str" || tree.kind === "num" || tree.kind === "bool" || tree.kind === "named" ||
    tree.kind === "date" || tree.kind === "time" || tree.kind === "timestamp" || tree.kind === "money";
}

/**
 * The tree the checkpoint vm answers (vm.typeTree), read strictly: a kind this page does not know, or a
 * node missing what its kind needs, throws, naming where it is, so a form says it cannot show the type
 * rather than drop the field. FEATURE-20261029-wasm-checkpoint-interpreter.
 */
export function readTypeTree(raw: unknown, at = "the configuration"): TypeTree {
  const t = asObject(raw);
  if (!t || typeof t["kind"] !== "string") throw new Error(`${at}: not a type tree node: ${JSON.stringify(raw)}`);
  const kind = t["kind"];
  if (leafKinds.has(kind)) return { kind } as TypeTree;
  const name = typeof t["name"] === "string" ? t["name"] : undefined;
  const module = typeof t["module"] === "string" ? t["module"] : undefined;
  const fields = (rawFields: unknown, where: string): TypeTreeField[] => {
    if (rawFields === undefined || rawFields === null) return [];
    if (!Array.isArray(rawFields)) throw new Error(`${where}: fields are not a list`);
    return rawFields.map((item, i) => {
      const f = asObject(item);
      if (!f || typeof f["name"] !== "string") throw new Error(`${where}: field ${i} has no name`);
      return { name: f["name"], type: readTypeTree(f["type"], `${where}.${f["name"]}`), hasDefault: Boolean(f["hasDefault"]) };
    });
  };
  switch (kind) {
    case "list":
    case "nullable":
      return { kind, of: readTypeTree(t["of"], `${at} (${kind} of)`) };
    case "record": {
      const defaults = asObject(t["defaults"]);
      const defaultsError = typeof t["defaultsError"] === "string" ? t["defaultsError"] : "";
      return { kind, name, module, fields: fields(t["fields"], at), ...(defaults ? { defaults } : {}), ...(defaultsError ? { defaultsError } : {}) };
    }
    case "tuple":
      return { kind, fields: fields(t["fields"], at) };
    case "enum": {
      if (!Array.isArray(t["cases"])) throw new Error(`${at}: an enum with no cases`);
      const cases = t["cases"].map((item, i) => {
        const c = asObject(item);
        if (!c || typeof c["name"] !== "string") throw new Error(`${at}: case ${i} has no name`);
        return { name: c["name"], fields: fields(c["fields"], `${at}.${c["name"]}`) };
      });
      return { kind, name, module, cases };
    }
    case "named":
      if (!name) throw new Error(`${at}: a named type with no name`);
      return { kind, name, module };
    default:
      throw new Error(`${at}: this page does not know the type kind "${kind}"; reload the page, or deploy one built with the server's godev`);
  }
}
